import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  PLATFORM_ID,
  inject,
  signal,
  effect,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { Router } from "@angular/router";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { filter } from "rxjs";
import { VideoRealtimeService } from "./services/video-realtime.service";
import { VideoWebRtcService } from "./services/video-webrtc.service";
import { VideoIceService } from "./services/video-ice.service";
import { VideoStreamsComponent } from "./components/video-streams/video-streams.component";
import { VideoControlsComponent } from "./components/video-controls/video-controls.component";
import { VideoStatusComponent } from "./components/video-status/video-status.component";
import { APP_NAME, APP_CONSTANTS } from "../../core/constants/app.constants";
import { BrowserStorageService } from "../../core/services/browser-storage.service";
import type {
  IceServerConfig,
  VideoMatch,
  VideoSession,
  VideoStatus,
} from "./models/video.models";

const SESSION_KEY = "strangeryap:video-session";

const FALLBACK_ICE: IceServerConfig[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19478" },
  { urls: "stun:stun3.l.google.com:19302" },
];

@Component({
  selector: "app-video-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    VideoStreamsComponent,
    VideoControlsComponent,
    VideoStatusComponent,
  ],
  // Component-scoped providers: destroyed when user navigates away, camera stops automatically
  providers: [VideoRealtimeService, VideoWebRtcService, VideoIceService],
  templateUrl: "./video-page.component.html",
  styleUrl: "./video-page.component.scss",
})
export class VideoPageComponent {
  readonly appName = APP_NAME;
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storage = inject(BrowserStorageService);

  readonly realtime = inject(VideoRealtimeService);
  readonly webrtc = inject(VideoWebRtcService);
  private readonly ice = inject(VideoIceService);

  readonly status = signal<VideoStatus>("onboarding");
  private session: VideoSession | null = null;
  private connectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Onboarding form state
  readonly displayName = signal("");
  readonly selectedInterests = signal<string[]>([]);
  readonly interests = APP_CONSTANTS.interests;

  // Peer info shown in the header while connected
  readonly name = signal("Stranger");
  readonly country = signal("");

  // Pending join flag: set before connect() so the effect fires join() on first connect
  private readonly pendingJoin = signal(false);

  constructor() {
    // Auto-join on socket connect when pendingJoin is true
    effect(() => {
      if (this.realtime.connected() && this.pendingJoin()) {
        this.pendingJoin.set(false);
        this.joinQueue();
      }
    });

    // Match found → start WebRTC handshake
    toObservable(this.realtime.match)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((match) => {
        this.name.set(match.peer.displayName || "Stranger");
        this.country.set(match.peer.country || "");
        this.onMatchFound(match);
      });

    // Callee: offer received from peer
    toObservable(this.realtime.incomingOffer)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((sdp) => this.onOfferReceived(sdp));

    // Caller: answer received from peer
    toObservable(this.realtime.incomingAnswer)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((sdp) => this.onAnswerReceived(sdp));

    // ICE candidates trickle in from peer
    toObservable(this.realtime.incomingIceCandidate)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((candidate) => this.webrtc.addIceCandidate(candidate));

    // Peer left or skipped → reset WebRTC + show brief status + auto-rejoin
    toObservable(this.realtime.peerEvent)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe((event) => {
        this.webrtc.resetForNewCall();
        this.status.set(event === "skipped" ? "peer-skipped" : "peer-left");
        setTimeout(() => {
          if (
            this.status() === "peer-skipped" ||
            this.status() === "peer-left"
          ) {
            this.joinQueue();
          }
        }, 1800);
      });

    // WebRTC connection state changes
    toObservable(this.webrtc.connectionState)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        if (state === "connected") {
          this.clearConnectionTimeout();
          this.status.set("connected");
        } else if (state === "failed" && this.status() === "connected") {
          // 'disconnected' is temporary and can self-heal — only treat 'failed' as terminal
          this.webrtc.resetForNewCall();
          this.status.set("peer-left");
          setTimeout(() => {
            if (this.status() === "peer-left") this.joinQueue();
          }, 1800);
        }
      });

    // Wire outgoing ICE candidates from WebRTC service to the socket
    this.webrtc.onIceCandidate = (candidate) => {
      const roomId = this.realtime.roomId();
      if (roomId) this.realtime.sendIceCandidate(roomId, candidate);
    };

    // Restore saved session (skip onboarding for returning users)
    if (isPlatformBrowser(this.platformId)) {
      const saved = this.loadSession();
      if (saved) {
        this.session = saved;
        this.displayName.set(saved.displayName);
        this.selectedInterests.set(saved.interests);
        this.initFromSession(saved);
      }
    }

    this.destroyRef.onDestroy(() => {
      this.realtime.disconnect();
      // VideoWebRtcService.ngOnDestroy handles camera stop + peer connection close
    });
  }

  // ─── Onboarding ───────────────────────────────────────────────────────────
  async completeOnboarding() {
    const name = this.displayName().trim() || "Stranger";
    const sess: VideoSession = {
      userId: crypto.randomUUID(),
      displayName: name,
      avatarUrl: null,
      interests: this.selectedInterests(),
    };
    this.session = sess;
    this.saveSession(sess);
    await this.initFromSession(sess);
  }

  toggleInterest(interest: string) {
    this.selectedInterests.update((list) => {
      if (list.includes(interest)) return list.filter((i) => i !== interest);
      if (list.length >= APP_CONSTANTS.maxInterests) return list;
      return [...list, interest];
    });
  }

  // ─── Navigation ───────────────────────────────────────────────────────────
  home() {
    this.router.navigateByUrl("/");
  }

  // ─── Queue control ────────────────────────────────────────────────────────
  joinQueue() {
    this.status.set("waiting");
    this.realtime.join(this.selectedInterests());
  }

  cancelSearch() {
    this.stopAndGoHome();
  }

  skip() {
    this.clearConnectionTimeout();
    const roomId = this.realtime.roomId();
    this.webrtc.resetForNewCall();
    if (roomId) {
      this.realtime.skip(roomId, this.selectedInterests());
    } else {
      this.joinQueue();
    }
    this.status.set("waiting");
  }

  end() {
    this.stopAndGoHome();
  }

  private stopAndGoHome(): void {
    this.clearConnectionTimeout();
    const roomId = this.realtime.roomId();
    this.webrtc.resetForNewCall();
    if (roomId) {
      // Notify peer via skip protocol before disconnecting
      this.realtime.skip(roomId, []);
      setTimeout(() => this.realtime.disconnect(), 100);
    } else {
      this.realtime.cancel();
    }
    this.status.set("onboarding");
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId !== null) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
  }

  // ─── Private: session bootstrap ───────────────────────────────────────────
  private async initFromSession(sess: VideoSession) {
    this.status.set("requesting-permissions");
    const result = await this.webrtc.requestMedia();
    if (result === "denied") {
      this.status.set("permission-denied");
      return;
    }
    this.pendingJoin.set(true);
    this.realtime.connect(sess.userId, sess.displayName, "", sess.avatarUrl);
    // effect() picks up pendingJoin + connected and calls joinQueue()
  }

  // ─── Private: WebRTC orchestration ────────────────────────────────────────
  private async onMatchFound(match: VideoMatch) {
    this.clearConnectionTimeout();
    this.status.set("connecting");
    try {
      // Create PC immediately with STUN so callee can receive an offer right away,
      // before the async TURN credential fetch completes.
      this.webrtc.createPeerConnection(FALLBACK_ICE);

      if (match.role === "caller") {
        // Caller: wait for full ICE config (includes TURN), then recreate before sending offer.
        // No offer has been sent yet so recreating PC is safe.
        const iceServers = await this.ice.getIceServers(this.session!.userId);
        this.webrtc.createPeerConnection(iceServers);
        const offer = await this.webrtc.createOffer();
        this.realtime.sendOffer(match.roomId, offer);
      } else {
        // Callee: fetch TURN creds in background; only recreate if offer hasn't arrived yet
        // (i.e. remote description not yet set). If offer beats us, STUN-only is still fine.
        this.ice.getIceServers(this.session!.userId).then((iceServers) => {
          if (!this.webrtc.hasRemoteDescription()) {
            this.webrtc.createPeerConnection(iceServers);
          }
        });
      }

      // If P2P handshake doesn't complete in 12s, give up and find someone new.
      this.connectionTimeoutId = setTimeout(() => {
        if (this.status() === "connecting") {
          this.webrtc.resetForNewCall();
          this.joinQueue();
        }
      }, 12_000);
    } catch (err) {
      console.error("[video] WebRTC setup failed", err);
      this.joinQueue();
    }
  }

  private async onOfferReceived(sdp: RTCSessionDescriptionInit) {
    const roomId = this.realtime.roomId();
    if (!roomId) return;
    try {
      const answer = await this.webrtc.handleOffer(sdp);
      this.realtime.sendAnswer(roomId, answer);
    } catch (err) {
      console.error("[video] handleOffer failed", err);
    }
  }

  private async onAnswerReceived(sdp: RTCSessionDescriptionInit) {
    try {
      await this.webrtc.handleAnswer(sdp);
    } catch (err) {
      console.error("[video] handleAnswer failed", err);
    }
  }

  // ─── Private: session persistence ─────────────────────────────────────────
  private saveSession(sess: VideoSession) {
    this.storage.setItem(SESSION_KEY, JSON.stringify(sess));
  }

  private loadSession(): VideoSession | null {
    try {
      const raw = this.storage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as VideoSession) : null;
    } catch {
      return null;
    }
  }
}
